import React, { useEffect, useState, useCallback } from "react";
import Select, { type Props } from "react-select";
import debounce from "lodash/debounce";
import { ErrorOutlineOutlined } from "@material-ui/icons";

import { useFetchPlaceAutocomplete } from "models/new";
import { format } from "lib/globalize-util";
import { STYLE_TOKENS } from "../style-tokens";

import { DATATEST } from "./constants";
import { formatOptionLabel, Input } from "./helpers";
import { useStyles, selectCustomStyles } from "./place-autocomplete.styles";

type TPlaceAutocompleteSelectedOption = {
    value: string;
    label: string;
    highlightValue: string;
} | null;

export type TPlaceAutocompleteFormattedSelectedOption =
    | { error: string }
    | null
    | {
          addressLine1: string;
          addressLine2: string;
          city: string;
          state: string;
          zip: string;
          countryCode: string;
          formattedAddress: string;
      };

export type TPlaceAutocompleteState = {
    isTouched: boolean;
    input: string;
    options: {
        value: string;
        label: string;
    }[];
    selectedOption: TPlaceAutocompleteSelectedOption;
    formattedSelectedOption: TPlaceAutocompleteFormattedSelectedOption;
};

type TPlaceAutocompleteProps =
    | Props
    | {
          name: string;
          countryCode?: string;
          value?: string;
          label?: string;
          placeholder?: string;
          isRequired?: boolean;
          autoFocus?: boolean;
          hasError?: boolean;
          isDisabled?: boolean;
          isClearable?: boolean;
          onChange: (option: TPlaceAutocompleteFormattedSelectedOption) => void;
      };

export const MANUAL_INPUT = "MANUAL_INPUT";

export const PlaceAutocomplete = (props: TPlaceAutocompleteProps) => {
    const styles = useStyles();
    const fetchPlaceAutocomplete = useFetchPlaceAutocomplete();
    const [state, setState] = useState<TPlaceAutocompleteState>({
        isTouched: false,
        input: props.value || "",
        options: [],
        selectedOption: null,
        formattedSelectedOption: null,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetchPlaceAutocomplete = useCallback(
        debounce(({ countryCode, address }) => {
            fetchPlaceAutocomplete.fetch({
                countryCode,
                address,
            });
        }, 1500),
        [],
    );

    useEffect(() => {
        if (state.input && props.countryCode) {
            debouncedFetchPlaceAutocomplete({
                countryCode: props.countryCode,
                address: state.input,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.input, props.countryCode]);

    useEffect(() => {
        if (
            fetchPlaceAutocomplete.loaded &&
            fetchPlaceAutocomplete.data &&
            fetchPlaceAutocomplete.data.places.length &&
            state.input
        ) {
            const options = fetchPlaceAutocomplete.data.places.map((place) => ({
                value: place.formattedAddress,
                label: place.formattedAddress,
                highlightValue: state.input,
            }));

            options.push({
                value: MANUAL_INPUT,
                label: `${format("Can't see your address?")} <span style='text-decoration: underline; color: "${STYLE_TOKENS.COLORS.colRaven}";'>${format("Enter it manually")}.</span>`,
                highlightValue: state.input,
            });

            setState((prevState: TPlaceAutocompleteState) => ({ ...prevState, options }));
        } else {
            setState((prevState: TPlaceAutocompleteState) => ({ ...prevState, options: [] }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchPlaceAutocomplete.loaded, fetchPlaceAutocomplete.data]);

    useEffect(() => {
        if (fetchPlaceAutocomplete.error) {
            setState((prevState: TPlaceAutocompleteState) => ({
                ...prevState,
                options: [],
                selectedOption: undefined,
                formattedSelectedOption: null,
            }));

            props.onChange({ error: fetchPlaceAutocomplete.error });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchPlaceAutocomplete.error]);

    const getResponseMessage = () => {
        const haveNoAdresses =
            fetchPlaceAutocomplete.loaded &&
            Boolean(fetchPlaceAutocomplete.data) &&
            !fetchPlaceAutocomplete.data.places.length;

        let message = format("Start typing to get address suggestions.");

        if (fetchPlaceAutocomplete.error) {
            message = format(
                "There was an error finding your address, please try again or try entering your values manually.",
            );
        } else if (haveNoAdresses) {
            message = format(
                "No results found. Please try adding more characters, selecting a country or entering values manually.",
            );
        }

        return <div data-test={DATATEST.NO_OPTIONS}>{message}</div>;
    };

    const handleInputChange = (inputValue: string, { action }: { action: string }) => {
        if (action === "input-change") {
            setState((prevState: TPlaceAutocompleteState) => ({
                ...prevState,
                isTouched: true,
                input: inputValue,
                options: inputValue ? prevState.options : [],
                selectedOption: inputValue ? prevState.selectedOption : undefined,
                formattedSelectedOption: inputValue ? prevState.formattedSelectedOption : null,
            }));
        }
    };

    const handleChange = (option: TPlaceAutocompleteSelectedOption, { action }: { action: string }) => {
        if (action === "clear") {
            setState((prevState: TPlaceAutocompleteState) => ({
                ...prevState,
                input: "",
                options: [],
                selectedOption: undefined,
                formattedSelectedOption: null,
            }));

            props.onChange(null);
        } else {
            const foundPlace = fetchPlaceAutocomplete.data.places.find(
                (place) => place.formattedAddress === option.value,
            );

            // CustomAddress
            if (option.value === MANUAL_INPUT) {
                setState((prevState: TPlaceAutocompleteState) => {
                    const formattedSelectedOption = {
                        addressLine1: prevState.input,
                        addressLine2: "",
                        city: "",
                        state: "",
                        zip: "",
                        countryCode: "",
                        formattedAddress: "",
                    };

                    props.onChange(formattedSelectedOption);

                    return {
                        ...prevState,
                        selectedOption: option,
                        formattedSelectedOption,
                    };
                });
            }

            // Select Address
            if (foundPlace) {
                const formattedSelectedOption = {
                    addressLine1: foundPlace.addressLine1,
                    addressLine2: "",
                    city: foundPlace.city,
                    state: foundPlace.state,
                    zip: foundPlace.zip,
                    countryCode: foundPlace.countryCode,
                    formattedAddress: foundPlace.formattedAddress,
                };

                setState((prevState: TPlaceAutocompleteState) => ({
                    ...prevState,
                    selectedOption: option,
                    formattedSelectedOption,
                }));

                props.onChange(formattedSelectedOption);
            }
        }
    };

    const isFieldRequiredError = props.isRequired && state.isTouched && state.input.trim().length === 0;
    const hasError = isFieldRequiredError || Boolean(fetchPlaceAutocomplete.error);

    return (
        <div className={styles.root} data-test={DATATEST.ROOT}>
            {props.label && (
                <label className={styles.label}>
                    {props.label} {Boolean(props.isRequired) && <span className={styles.asterix}>*</span>}
                </label>
            )}
            <Select
                {...props}
                name={props.name}
                // to show placeholder programmatically need to clear React-Select
                key={`REACT-SELECT-KEY__${props.name}__${state.selectedOption}`}
                value={state.selectedOption}
                inputValue={state.input}
                placeholder={props.placeholder || ""}
                options={state.options}
                isLoading={fetchPlaceAutocomplete.isLoading}
                formatOptionLabel={formatOptionLabel}
                components={{ Input }}
                autoFocus={Boolean(props.autoFocus)}
                isClearable={Boolean(props.isClearable)}
                isDisabled={Boolean(props.isDisabled) || !props.countryCode}
                filterOption={null}
                noOptionsMessage={getResponseMessage}
                onInputChange={handleInputChange}
                onChange={handleChange}
                styles={selectCustomStyles({ hasError })}
            />
            {isFieldRequiredError && (
                <div className={styles.error}>
                    <ErrorOutlineOutlined className={styles.errorIcon} />
                    {format("This field is required.")}
                </div>
            )}
        </div>
    );
};
